<template>
	<DxScheduler :data-source="dataItemsSource" :current-date="currentDate" height="100%" :views="['day', 'week', 'month']"
		:current-view="currentView" :show-current-time-indicator="true" :adaptivity-enabled="false"
		@content-ready="onContentReady" @appointment-updated="onAppointmentUpdated"
	>
		<DxEditing :allow-adding="true" :allow-dragging="true" :allow-resizing="true" :allow-updating="true" :allow-editing="true" />
	</DxScheduler>
</template>

<script setup>
/* eslint-disable */
import { defineProps, ref, watch, nextTick, inject, computed } from 'vue';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from "devextreme/data/data_source"
import { DxScheduler, DxEditing } from 'devextreme-vue/scheduler';
import { fieldNamesReserved } from '@/fields';
import { SchemaCache } from '@/schemas.js';

const VALID_DATE_REGEX = new RegExp(String.raw`^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)?$`);
const $http = inject('$http');

const props = defineProps({
	preview: Boolean,
	settings: Object,
	context: Object,
});
const currentDate = ref(new Date());
const currentView = ref('month');

// Effective bindings: FromDate, Title. Stored binding path uses full keyspec, e.g. customer.name instead of just name - so we extract the last part.
const bindings = computed(() => {
	return {
		fromDate: props.settings.bindFromDate ? props.settings.bindFromDate.split('.').pop() : '',
		title: props.settings.bindTitle ? props.settings.bindTitle.split('.').pop() : '',
	}
});

const dataColumns = ref([]);
const dataItemsSource = new DataSource({
	store: new CustomStore({
		key: 'id',
		loadMode: 'raw',
		cacheRawData: true,
		load: async (loadOptions) => {
			if (!props.settings || !props.settings.source)
				return [];
			let fetchRequest = await $http.get('/mote', { params: { type: props.settings.source } });
			let result = fetchRequest.data.map(({ payload, ...datum }) => ({
				...(payload || {}),
				...datum
			}));
			console.log('ux-calendar dataItems:', result, loadOptions);
			// TODO: Implement per-monthly or per-yearly fetching of data?
			console.log(result);
			return result;
		},
		insert: async (values) => {},
		update: async (key, values) => {
			// TODO: This is a hack to get the original item. Once we use the proper non-raw data source, check how this is supposed to properly work!
			const originalItem = dataItemsSource['_items'].find(item => item.id == key);
			if (!originalItem || !originalItem['_mote']) {
				throw new Error('Could not find original item for update; save aborted!');
			}
			
			const schema = SchemaCache.getSchemaByType(props.settings.source);
			if (!schema) {
				throw new Error('Could not find schema for update; save aborted!');
			}
			const groupId = props.context?.group_id ?? null;
			if (!groupId) {
				throw new Error(!props.context ? 'Update skipped - previewing component' : 'Could not find group for update; save aborted!');
			}
			// TODO: Deal with schema actions, defaults, etc.
			
			// Create our updated PATCH data, where we include sequence and base fields and put the custom values in payload.
			const patchData = {
				id: key,
				sequence: originalItem['_mote'].sequence + 1,
				payload: {},
			};
			// TODO: Handle mapping from bound fields back to our mote values (in originalItem['_mote'])
			for (const [key, value] of Object.entries(values)) {
				if (fieldNamesReserved.includes(key))		// Ignore attempted changes to non-field entries.
					continue;
				// TODO: Drop any values that don't resolve to a field we understand on this schema.
				patchData.payload[key] = value;
			}

			console.log(patchData);
			return;

			const response = await $http.patch(`/mote/${key}`, patchData);
			return response.data;
		},
		remove: async (key) => {
			// TODO: This is a hack to get the original item. Once we use the proper non-raw data source, check how this is supposed to properly work!
			const originalItem = dataItemsSource['_items'].find(item => item.id == key);
			if (!originalItem || !originalItem['_mote']) {
				throw new Error('Could not find original item for delete; delete aborted!');
			}
			
			const schema = SchemaCache.getSchemaByType(props.settings.source);
			if (!schema) {
				throw new Error('Could not find schema for delete; delete aborted!');
			}
			const groupId = props.context?.group_id ?? null;
			if (!groupId) {
				throw new Error(!props.context ? 'Delete skipped - previewing component' : 'Could not find group for delete; delete aborted!');
			}

			const response = await $http.delete(`/mote/${key}`, { data: { sequence: originalItem['_mote'].sequence } });
			
			// Reload the data source to refresh the UI
			dataItemsSource.reload();
			
			return response.data;
		},
	}),
	map: function(item) {
		// NB: Date and DateTime fields are always stored as UTC ISO 8601, such as 2025-02-21T00:00:00Z or 2025-02-21T00:00:00.000Z.
		let startingDate = item[bindings.value.fromDate] ?? null;
		let hasTimes = startingDate && String(startingDate).includes('T');		// TODO: What if endingDate has times but startingDate doesn't, or vice versa?
		// TODO: Normalize the data; if it is a Date field we don't want to have extra 'time' values!
		if (startingDate && !VALID_DATE_REGEX.test(startingDate)) {
			console.warn('Ignoring invalid date: ', startingDate);
			startingDate = null;
		}
		
		return {
			id: item.id,
			text: bindings.value.title ? String(item[bindings.value.title]) : "" ?? "",
			allDay: !hasTimes,
			startDate: startingDate ? new Date(startingDate) : undefined ?? undefined,
			endDate: undefined,
			'_mote': item,
			// recurrenceRule: 'FREQ=WEEKLY',
		};
	}
});

function onAppointmentUpdated(e) {
	// console.log('onAppointmentUpdated', e);
}

let executingContentReady = false;			// Make sure we don't infinite loop when we update currentDate during onContentReady!
const onContentReady = (e) => {
	if (executingContentReady)
		return;
	executingContentReady = true;
	// console.log('onContentReady', e);
	// currentDate.value = new Date();
	nextTick(() => {
		executingContentReady = false;
	});
}

watch(props, async () => {
	// TODO: Implement data source fetching!
	dataColumns.value.length = 0;
	await SchemaCache.waitForData();
	var columns = props.settings.source ? (SchemaCache.getSchemaByType(props.settings.source)?.fields ?? []) : [];
	dataColumns.value = columns.map(c => c.field);

	dataItemsSource.filter(null);
	if (props.settings && props.settings.filter) {
		dataItemsSource.filter(props.settings.filter);
	}

	if (props.settings && props.settings.startingDate) {
		if (props.settings.startingDate == 'now') {
			currentDate.value = new Date();
		} else {
			// Cases recent_now, recent, oldest.
			// TODO: How can we do this? Aggregation needs to be happening on server side, before we even fetch the CustomStore data!
			console.log('Would set startingDate now.');
		}
	}

	dataItemsSource.load();
}, {
	immediate: true
});

</script>
